import { AssessorService } from './assessor.service';
import { IAssessorProfileDTO } from '../../models/assessor/dto/assessor-profile.dto';
import { StateService } from '../state/state.service';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { StatusCodes } from 'http-status-codes';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

/**
 * Resolves and caches the marketplace profile id for the logged-in user.
 */
@Injectable({ providedIn: 'root' })
export class CurrentAssessorProfileService {
  private readonly _assessorService = inject(AssessorService);
  private readonly _stateService = inject(StateService);
  private _profileId$: Observable<string | null> | null = null;
  private _userId = '';

  /**
   * Finds the current user's assessor profile id.
   * @returns {Observable<string | null>} Profile id, or null when the user has no profile.
   */
  public profileId$(): Observable<string | null> {
    const userId = (this._stateService.currentUserId() ?? '').trim();

    if (userId === '') {
      return of(null);
    }

    if (this._profileId$ === null || this._userId !== userId) {
      this._userId = userId;
      this._profileId$ = this._findProfileId$(userId).pipe(
        tap((profileId): void => {
          if (profileId === null) {
            this._profileId$ = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((error: unknown): Observable<never> => {
          this._profileId$ = null;

          return throwError((): unknown => error);
        })
      );
    }

    return this._profileId$;
  }

  /**
   * Loads the marketplace profile for one user id.
   * A 404 means this user has no assessor profile.
   * @param {string} userId - Logged-in user id.
   * @returns {Observable<string | null>} Matching profile id, or null when the profile is missing.
   */
  private _findProfileId$(userId: string): Observable<string | null> {
    return this._assessorService.getAssessorProfile$(userId, '?type=auditor').pipe(
      map((profile: IAssessorProfileDTO): string | null => {
        const profileId = profile.id.trim();

        return profileId === '' ? null : profileId;
      }),
      catchError((error: unknown): Observable<string | null> => {
        if (error instanceof HttpErrorResponse && error.status === StatusCodes.NOT_FOUND) {
          return of(null);
        }

        return throwError((): unknown => error);
      })
    );
  }
}
